import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Send, Bot, User as UserIcon, Loader2,
    MessageSquare, ShieldCheck, Zap, Maximize2, Minimize2,
    RotateCcw, Trash2, ArrowUpRight
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { backendURL } from '../../config';

export default function CopilotPanel({ isOpen, onClose }) {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: "👋 **Hello Analyst!** I am your **QueryTel Copilot**, connected live to your FortiGate & FortiAnalyzer telemetry stream (500,000+ logs analyzed).\n\nHow can I assist you with threat intelligence, log queries, or posture analysis today?"
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isLoading]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMsg = { role: 'user', content: input };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await axios.post(`${backendURL}/api/analysis/chat`, {
                message: input,
                history: messages.slice(-10)
            });

            const aiResponse = response.data.response;
            setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
        } catch (error) {
            console.error("Copilot Error:", error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "⚠️ I encountered an error communicating with the Neural Core. Please ensure the AI service is operational."
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const clearHistory = () => {
        setMessages([{
            role: 'assistant',
            content: "Neural core reset. Session history purged. How can I help you now?"
        }]);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ x: 400, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 400, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className={`fixed right-0 top-0 h-screen bg-[#111827] border-l border-white/[0.06] shadow-[-20px_0_50px_rgba(0,0,0,0.5)] z-[60] flex flex-col transition-all duration-300 ${isMinimized ? 'w-20' : 'w-[480px]'}`}
            >
                {/* Header */}
                <div className="p-6 border-b border-white/[0.06] flex items-center justify-between bg-blue-600/5">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center border border-blue-500/30">
                            <Bot className="w-6 h-6 text-blue-400" />
                        </div>
                        {!isMinimized && (
                            <div>
                                <h3 className="text-sm font-black text-slate-100 uppercase tracking-tighter italic">QueryTel Copilot</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Neural Link Active</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={clearHistory}
                            className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                            title="Clear History"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-500 hover:text-blue-600 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {!isMinimized ? (
                    <>
                        {/* Messages Area */}
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-white/10"
                        >
                            {messages.map((msg, i) => (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    key={i}
                                    className={`flex gap-3.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                                >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0 mt-0.5 ${msg.role === 'user'
                                            ? 'bg-[#1e293b] border-white/[0.06]'
                                            : 'bg-blue-600/10 border-blue-500/20'
                                        }`}>
                                        {msg.role === 'user' ? <UserIcon className="w-4 h-4 text-slate-400" /> : <ShieldCheck className="w-4 h-4 text-blue-400" />}
                                    </div>
                                    <div className={`flex flex-col max-w-[88%] ${msg.role === 'user' ? 'items-end' : ''}`}>
                                        <div className={`px-4 py-3 rounded-2xl text-[13px] leading-relaxed ${msg.role === 'user'
                                                ? 'bg-blue-600 text-slate-100 rounded-tr-none shadow-[0_0_20px_rgba(37,99,235,0.2)]'
                                                : 'bg-[#1a2332] text-slate-200 border border-white/[0.06] rounded-tl-none shadow-lg'
                                            }`}>
                                            {msg.role === 'user' ? (
                                                <div className="whitespace-pre-wrap">{msg.content}</div>
                                            ) : (
                                                <div className="copilot-markdown space-y-2">
                                                    <ReactMarkdown
                                                        components={{
                                                            p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed text-slate-200">{children}</p>,
                                                            strong: ({ children }) => <strong className="font-semibold text-slate-100">{children}</strong>,
                                                            em: ({ children }) => <em className="italic text-blue-300 not-italic font-medium">{children}</em>,
                                                            ul: ({ children }) => <ul className="my-2 pl-4 list-disc space-y-1 text-slate-300">{children}</ul>,
                                                            ol: ({ children }) => <ol className="my-2 pl-4 list-decimal space-y-1 text-slate-300">{children}</ol>,
                                                            li: ({ children }) => <li className="text-slate-300 leading-normal">{children}</li>,
                                                            code: ({ inline, children }) => (
                                                                <code className="px-1.5 py-0.5 rounded bg-blue-950/70 text-blue-300 font-mono text-[11px] border border-blue-800/40">
                                                                    {children}
                                                                </code>
                                                            ),
                                                            a: ({ href, children }) => {
                                                                const isInternal = href && href.startsWith('/');
                                                                return (
                                                                    <a
                                                                        href={href}
                                                                        onClick={(e) => {
                                                                            if (isInternal) {
                                                                                e.preventDefault();
                                                                                navigate(href);
                                                                                onClose();
                                                                            }
                                                                        }}
                                                                        className="inline-flex items-center gap-0.5 text-blue-400 hover:text-blue-300 underline font-medium cursor-pointer transition-colors"
                                                                    >
                                                                        {children}
                                                                        {isInternal && <ArrowUpRight className="w-3 h-3 inline" />}
                                                                    </a>
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {msg.content}
                                                    </ReactMarkdown>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                            {isLoading && (
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
                                        <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                                    </div>
                                    <div className="bg-[#1a2332] px-4 py-3 rounded-2xl border border-white/[0.06] rounded-tl-none">
                                        <div className="flex gap-1">
                                            <span className="w-1.5 h-1.5 bg-blue-500/40 rounded-full animate-bounce"></span>
                                            <span className="w-1.5 h-1.5 bg-blue-500/40 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                                            <span className="w-1.5 h-1.5 bg-blue-500/40 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Input Area */}
                        <div className="p-6 border-t border-white/[0.06] bg-[#1a2332]">
                            <form onSubmit={handleSendMessage} className="relative group">
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Ask anything about security..."
                                    className="w-full bg-[#111827] border border-white/[0.06] rounded-2xl pl-12 pr-14 py-4 text-sm font-medium text-slate-100 placeholder:text-slate-600 outline-none focus:border-blue-500/50 transition-all shadow-inner"
                                />
                                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                    <MessageSquare className="w-5 h-5 text-slate-500 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                                <button
                                    type="submit"
                                    disabled={!input.trim() || isLoading}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-500 text-slate-100 rounded-xl flex items-center justify-center transition-all shadow-[0_0_15px_rgba(37,99,235,0.3)]"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </form>
                            <div className="mt-4 flex items-center justify-center gap-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">
                                <span className="flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> Llama 3.2 Engine</span>
                                <span>•</span>
                                <span className="flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> Encrypted Session</span>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center gap-6 overflow-hidden">
                        <button
                            onClick={() => setIsMinimized(false)}
                            className="w-10 h-10 bg-blue-600/10 border border-blue-500/30 rounded-xl flex items-center justify-center text-blue-400 hover:bg-blue-500 hover:text-blue-600 transition-all shadow-lg"
                        >
                            <Maximize2 className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
}
